import Ajax from "../Ajax"
import { store } from "./Store"
import { Alert, AlertModal, version } from ".."
import { NodeStore } from "./NodeStore"
import { ToolStore } from "./ToolStore"
import { ReportStore } from "./ReportStore"
import { EnquiryStore } from "./EnquiryStore"
import { QuestionStore } from "./QuestionStore"
import { application_status, case_status_color, case_target_date_description, case_type, countries_of_birth, dict_key, template_key, yes_no_outcome } from "../misc/DisplayDicts"
import { SessionInfo, Workflow } from "../models/Interfaces"
import { StateChange } from "../models/StateChange"
import { makeAutoObservable, reaction, runInAction } from "mobx"
import { ConnectionStore } from "./ConnectionStore"
import { AutomationStore } from "./AutomationStore"
import { QuestionnaireStore } from "./QuestionnaireStore"
import { UTCDate } from "@date-fns/utc"


export class AppStore {

    appLoaded = false
    currentPanel: string = "FOLDER"
    sessionState: string = ""
    sessionInfo: SessionInfo | null = null
    displayDictionaries: any;
    token: string | null = window.localStorage.getItem("aora_jwt")
    workflows: Workflow[] = null

    navigate: (string: string) => void
    location: Location;
    // reader = new OnDemandLiveRegion() // Screen reader

    constructor() {

        makeAutoObservable(this)

        // This is run when this.token changes so we can control the token
        // in browser storage via this property

        reaction(() => this.token, token => {

            token
                ? window.localStorage.setItem("aora_jwt", token)
                : window.localStorage.removeItem("aora_jwt")

        })
    }

    resetApp = () => {

        this.setAppLoaded(false)
        this.navigate("/")
        this.setToken(null)
        this.setPageTitle("AORA")

        store.AutomationStore.reset()
        store.NodeStore = new NodeStore()
        store.ToolStore = new ToolStore()
        store.AlertStore.alertModals = []
        // store.AlertStore = new AlertStore()
        store.ReportStore = new ReportStore()
        store.EnquiryStore = new EnquiryStore()
        store.QuestionStore = new QuestionStore()
        store.ConnectionStore = new ConnectionStore()
        store.AutomationStore = new AutomationStore()
        store.QuestionnaireStore = new QuestionnaireStore()

        this.currentPanel = "FOLDER"
        this.sessionState = ""
        this.sessionInfo = null
    }

    get isLoggedIn (): boolean {
        return !!this.token
    }

    get stateAction (): string {

        var value = this.sessionState?.split(" ").length > 1
            ? this.sessionState?.split(" ")[0]
            : null

        if (value?.split("_").length > 0) {
            value = value?.split("_")[0]
        }

        return value
    }

    get stateSubject (): string {
        var value = this.sessionState?.split(" ").length > 1 ? this.sessionState?.split(" ")[1] : ""
        value.replace("Node", "")
        return value
    }

    get readOnly (): boolean {
        var isSubAccount = this.checkJwtRoles(["client_account", "sponsor_account"], false)
        if (isSubAccount && !this.checkJwtRole("self_advisor")) {
            return true
        }
        return false
    }

    init = async () => {

        await this.checkVersion()

        if (!window.location.href.includes("/app/")) {
            this.navigate("/app/main")
        }

        await Ajax.Session.Info().then((response) => {
            this.sessionInfo = response.data
        })

        await Ajax.Session.Language().then((response) => {
            this.displayDictionaries = {
                ...response.data, 
                dict_key: dict_key, 
                template_key: template_key, 
                application_status: application_status, 
                countries_of_birth: countries_of_birth, 
                yes_no_outcome: yes_no_outcome,
                case_type: case_type,
                case_status_color: case_status_color,
                case_target_date_description: case_target_date_description
            }
        })

        await this.getWorkflows()        

        await store.QuestionnaireStore.init()
        await store.NodeStore.init()
        await store.AutomationStore.init()

        this.setAppLoaded()

        try {
            var redirectUrl = null
            var originalRequestUrl = localStorage.getItem("redirect_after_login")
            if (originalRequestUrl) {
                console.log("Original page request:", originalRequestUrl)
                var slug = originalRequestUrl.split("/app/main/")
                if (slug.length > 1 && slug[1].length > 5) {
                    redirectUrl = `/app/main/${slug[1]}`
                    localStorage.removeItem("redirect_after_login")
                }
            }
            if (redirectUrl) {
                this.navigate(redirectUrl)
            }
        } catch (e) {
            console.log("Error when attempting to redirect to the original page request.")
        }
    }

    loginOauth = async () => {
        // Attempt to sign in user via OAuth using 'code' parameter in URI
        var code = this.getURLPeram(window.location.href, "code")
        var state = this.getURLPeram(window.location.href, "state")

        if (code && state) {
            console.log("Starting frontend with OAuth code")
            // this.setSubmittingOauthCode(true)
            var response = await Ajax.Session.LoginOAuth({code: code, state: state})
            this.setToken(response.data.token)
        }
    }

    logout = async () => {
        await Ajax.Session.Logout().finally(() => {
            this.resetApp()
        })
    }

    setToken = (token: string | null) => {
        this.token = token
    }

    setAppLoaded = (loaded = true) => {
        this.appLoaded = loaded
    }

    setPageTitle = (title: string) => {
        document.body.focus()
        document.body.blur()
        document.title = title
        // this.reader.say(title)
    }

    getWorkflows = async () => {
        await Ajax.Workflow.AllTemplates().then((response) => {
            this.workflows = response.data
        }).catch(() => {
            console.log("Failed to get workflows")
        })
    }

    checkVersion = async () => {

        const hardRefresh = async () => {
            var queryParams = new URLSearchParams(window.location.search);
            queryParams.set("lr", new Date().getSeconds().toString());        
            var query = queryParams.toString();                
            window.location.search = query;
        }

        await Ajax.Session.Version().then((response) => {
            var minVersionSplit = response.data?.split(".")
            var versionSplit = version.split(".")
            var usingOldVersion = false

            for (var i = 0; i < 3; i++) {
                if (parseInt(versionSplit[i]) < parseInt(minVersionSplit[i])){
                    usingOldVersion = true
                    break
                }
                if (parseInt(versionSplit[i]) > parseInt(minVersionSplit[i])){
                    usingOldVersion = false
                    break
                }
            }
            
            if (usingOldVersion) {
                AlertModal({title: "New version available", body: <p>You are using an old version of AORA.</p>, actions: [
                    {label: "Refresh for new version", color: "success", action: () => hardRefresh()}
                ]})
            }
        }).catch((response) => {
            console.log("Unable to check version", response)
        })
    }

    setSessionState = (stateChange: StateChange) => {

        console.log(`"${stateChange.previousState}" -> "${stateChange.newState}"`, stateChange)
        this.sessionState = stateChange.newState

        // Neutral state
        if (!!stateChange.newState === false) {
            store.QuestionStore.informationRequest = null
            store.QuestionStore.highlightUid = null
            store.QuestionStore.locatorPath = []
        }

        // Editing starting
        if (stateChange.newState?.toLowerCase().includes("editing")) {
            this.currentPanel = "QUESTION"
            // if (store.NodeStore.leafNode === null && window.innerWidth > 600 && this.stateSubject.) {
            //     setTimeout(() => {
            //         store.NodeStore.navigateNode(this.stateSubject)
            //     }, 1000)
            // }
        }

        // Creating ending
        if (stateChange.previousState?.toLowerCase().includes("creating")) {
            store.NodeStore.refreshFolderNode()
        }

        // Editing ending
        if (stateChange.previousState?.toLowerCase().includes("editing")) {
            store.NodeStore.refreshFolderNode()
            store.NodeStore.refreshLeafNode()
        }

        // Enquiry starting
        if (stateChange.newState?.toLowerCase().includes("running")) {
            // store.QuestionnaireStore.alert?.dismiss()
        }

        // Enquiry ending
        if (stateChange.previousState?.toLowerCase().includes("running")) {
            var enquiryUid = stateChange.previousState?.split(" ")[1]
            store.NodeStore.navigateNode(enquiryUid)
            store.NodeStore.refreshFolderNode()
        }
    }

    checkJwtRole = (value) => {

        var role = this.getJwtClaim("role")

        if (!Array.isArray(role)) {
            return false
        }

        return role.includes(value)
    }

    checkJwtRoles = (values: string[], all: boolean = true) => {

        var role = this.getJwtClaim("role")

        if (!Array.isArray(role)) {
            return false
        }

        var matchedValues = values.filter(value => role.includes(value))

        if ((all && matchedValues.length === values.length) || (!all && matchedValues.length)) {
            return true
        }

        return false
    }

    getJwtClaim = (name: string) => {

        const token = window.localStorage.getItem("aora_jwt")

        if (token) {
            var claims = this.decodeJWT()

            if (name in claims) {
                var value = claims[name]

                // role can return a string instead of a string array if there is only one item, so always return string array
                if (value && name === "role" && !Array.isArray(value)) {
                    return [value]
                }
                
                return value
            }
        }
    }

    decodeJWT = () => {

        var base64Url = this.token?.split('.')[1];
        var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        return JSON.parse(jsonPayload);
    }

    getCookie = (cname: string) => {

        let name = cname + "=";
        let decodedCookie = decodeURIComponent(document.cookie);
        let ca = decodedCookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) === 0) {
                return c.substring(name.length, c.length);
            }
        }
        return "";
    }

    setCookie = (cname: string, cvalue: any, exdays: any) => {

        const d = new UTCDate();
        d.setTime(d.getTime() + (exdays * 24 * 60 * 60 * 1000));
        let expires = "expires=" + d.toUTCString();
        document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
    }

    getURLPeram = (url: string, name: string) => {

        if (url && url.split("?").length > 1) {
            var query_string = url.split("?")[1]
            var perams = query_string.split("&")

            for (let i = 0; i < perams.length; i++) {
                if (perams[i].split("=")[0] === name && perams[i].split("=").length > 1) {
                    return decodeURI(perams[i].split("=")[1])
                }
            }
        }
        return null
    }

    DisplayDictionary = (dictionaryName: string, asValueAndLabel: boolean = false, order: "asc" | "desc" = "desc") => {

        var dictionaryNameCaseInsensitive = Object.keys(this.displayDictionaries).find((key) => {
            return key.toLowerCase() === dictionaryName.toLowerCase()
        })
        
        var displayDict = this.displayDictionaries[dictionaryNameCaseInsensitive]

        // Always return a dict
        if (!displayDict) {
            return asValueAndLabel ? [] : {}
        }

        // Return in format [ {value: "...", label: "..."} ]
        if (asValueAndLabel) {
            displayDict = Object.keys(displayDict).map((key, i) => {
                return {value: key, label: displayDict[key]}
            })
        }

        // Return as dict
        return displayDict
    }

    DisplayValue = (dictionaryName: string, keyName: string) => {

        var dictionaryNameCaseInsensitive = Object.keys(this.displayDictionaries).find((key) => {
            return key.toLowerCase() === dictionaryName.toLowerCase()
        })
        
        var displayDict = this.displayDictionaries[dictionaryNameCaseInsensitive]

        if (displayDict && Object.keys(displayDict).includes(keyName)) {
            return displayDict[keyName]
        }

        // Fallback to return keyName
        return keyName
    }
}